/* eslint-disable react/no-did-update-set-state */
import * as React from 'react';
import { connect } from 'react-redux';
import { isEmpty, isEqual, isNil, sortBy } from 'lodash';
import classnames from 'classnames';

import PageHeading from 'components/page/page-heading/PageHeading';
import Pagination from 'components/pagination/Pagination';
import Spinner from 'components/spinner/Spinner';
import TabsSection from 'components/tabs/tabs-section/TabsSection';
import ActionButton from 'components/buttons/action-button/ActionButton';
import ActionButtonDropdown from 'components/buttons/action-button-dropdown/ActionButtonDropdown';
import ActionLink from 'components/buttons/action-link/ActionLink';
import Tooltip from 'components/Tooltip';

import CampaignInfluencerSelector from 'modules/campaigns/components/CampaignInfluencerSelector';
import CampaignDeliverablesList from 'modules/campaigns/components/deliverables/CampaignDeliverablesList';
import CampaignDeliverablesTaskControl from 'modules/campaigns/components/deliverables/CampaignDeliverablesTaskControl';

import {
  downloadCampaignContent,
  fetchCampaignDeliverables,
  fetchDeliverableTags,
  resetDeliverables
} from 'modules/campaigns/reducers/campaignDeliverables';
import {
  fetchCampaignContentApprovalDeliverables,
  resetCampaignContentApprovalDeliverables
} from 'modules/campaigns/reducers/campaignContentApprovalDeliverables';
import {
  trackEventDeliverableUploaded
} from 'modules/tracking/utils/tracking';
import { fetchSubcampaigns, resetSubcampaigns } from '../../reducers/campaignSubcampaigns';
import { fetchInfluncerMetrics } from 'modules/influencers/reducers/influencersMetrics';

import { contentApprovalLinkSelector } from 'selectors/campaign';

import { __t, applyTranslations } from 'utils/language';
import { TASK_TAGS_COLORS, TASK_TAGS_COLORS_REVERSE, isAccepted } from 'utils/subcampaign';
import { replaceHistoryStateWithDefault } from 'utils/helper/common';
import {
  DELIVERABLE_TYPE_BLOG,
  DELIVERABLE_TYPE_CONTENT_PRODUCTION,
  DELIVERABLE_TYPE_ENTERTAINMENT_VIDEO,
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_OTHER,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO,
  buildPrefixForDownloadableCampaignContent,
  getDeliverableSelectViewItemsToIgnore,
  getDeliverablesCountByTypeFromOffer,
  getDeliverablesCountFromOffer,
  getDeliverablesSelectViewItems,
  isZalandoOutfitOnly
} from 'utils/deliverables';
import { getMostActiveState } from 'components/tabs/tabs-underlined/tabs-underlined-utils';
import { getHash, getUrlParam } from 'utils/url';

import './campaign-deliverables.scss';

import type {
  ActionButtonDropdownItem,
  DeliverableCampaign,
  DeliverableTagColor,
  DeliverableV2TabKey,
  DeliverablesFilterCampaign,
  LocationWindow,
  MixedSelectDropdownItem,
  Pagination as PaginationType,
  SelectOption,
  Subcampaign,
  SubcampaignInfluencerProfile
} from 'types';

applyTranslations(require('./campaign-deliverables.i18n.json'));

type OwnProps = {
  campaignId: number,
  subcampaignId: number | undefined | null,
  initialFilter: DeliverablesFilterCampaign | undefined | null
};

type StateProps = {
  deliverables: Array<DeliverableCampaign>,
  pagination: PaginationType,
  location: LocationWindow,
  isDeliverablesLoading: boolean,
  isSubcampaignsLoading: boolean,
  campaignName: string,
  subcampaignsList: Array<Subcampaign>,
  contentApprovalLink: string | undefined | null,
  deliverableTags: Array<{ name: string, color: DeliverableTagColor }>,
  contentApprovalV2: boolean
};

type DispatchProps = {
  dispatchFetchSubcampaigns: (campaignId: number) => Promise<any>,
  dispatchResetSubcampaigns: () => Promise<any>,
  dispatchFetchCampaignDeliverables: (
    campaignId: number,
    page?: number | null,
    subcampaignId?: number | null,
    deliverableType?: string | null,
    sort?: string | null,
    filter?: string,
    tags?: Array<string>,
    contentApprovalV2?: boolean
  ) => Promise<any>,
  dispatchResetDeliverables: () => Promise<any>,
  dispatchFetchCampaignContentApprovalDeliverables: (
    campaignId: number,
    selectedSubcampaignId?: number | null
  ) => Promise<any>,
  dispatchResetCampaignContentApprovalDeliverables: () => Promise<any>,
  dispatchDownloadCampaignContent: (
    prefix: string,
    campaignId: number,
    subcampaignId?: number | null,
    deliverableType?: string | null) => Promise<any>,
  dispatchDownloadAllApprovedCampaignContent: (
    prefix: string,
    campaignId: number,
    subcampaignId?: number | null,
    deliverableTypeFilter?: string | null) => Promise<void>,
  dispatchFetchInfluncerMetrics: (campaignId: number) => void,
  dispatchFetchDeliverableTags: (
    campaignId: number,
    deliverableType?: string | null,
    subcampaignId?: number | null,
    contentApprovalV2?: boolean
  ) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  selectedSubcampaignId: number | undefined | null,
  selectedSubcampaignCreator: SubcampaignInfluencerProfile | undefined | null,
  page: number,
  deliverableTypeFilter: string,
  sort: string,
  filter?: string,
  visibleSubcampaigns: Array<Subcampaign>,
  subcampaignsMapByInfluencer: {
    [x: number]: Subcampaign
  },
  fetchTriggered: boolean,
  filterOptions: Array<SelectOption>,
  tags: Array<MixedSelectDropdownItem>,
  tagsAsStrings: Array<string>,
  selectedViewIdsMap: {
    [id: number]: DeliverableV2TabKey
  },
  expandedIdsMap: {
    [id: number]: boolean
  }
};

const SORT_VALUES = ['start_date_asc', 'end_date_asc', 'content_creator_name_asc'];
const TAB_VALUES = ['content_production', 'rest'];
const TABS = TAB_VALUES.map(tabIdentifier => ({
  label: __t(`campaign_deliverables.tabs.${tabIdentifier}`),
  identifier: tabIdentifier
}));

const FILTER_OPTIONS: Array<DeliverablesFilterCampaign> = [
  'all',
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_BLOG,
  DELIVERABLE_TYPE_OTHER,
  DELIVERABLE_TYPE_ENTERTAINMENT_VIDEO
];

const resetButtonProps = {
  label: __t('campaign_deliverables.select_all_creators'),
  icon: 'users-connected-white-bg-custom-fg'
};

const DownloadButtonType = {
  ALL: 'all-content',
  APPROVED: 'approved-content'
};

class CampaignDeliverables extends React.Component<Props, State> {
  containerRef = React.createRef<HTMLElement>();

  state: State = {
    selectedSubcampaignId: this.props.subcampaignId,
    selectedSubcampaignCreator: null,
    visibleSubcampaigns: [],
    subcampaignsMapByInfluencer: {},
    deliverableTypeFilter: this.props.initialFilter || DELIVERABLE_TYPE_CONTENT_PRODUCTION,
    sort: SORT_VALUES[0],
    filter: undefined,
    page: 1,
    fetchTriggered: false,
    filterOptions: [],
    tags: this.getTags(this.props.deliverableTags.filter(tag => tag.name)),
    tagsAsStrings: [],
    selectedViewIdsMap: {},
    expandedIdsMap: {}
  };

  componentDidMount(): void {
    const {
      campaignId,
      contentApprovalV2,
      dispatchFetchSubcampaigns,
      dispatchFetchInfluncerMetrics,
      dispatchFetchDeliverableTags,
      dispatchFetchCampaignContentApprovalDeliverables
    } = this.props;
    const { deliverableTypeFilter, sort, filter, page, selectedSubcampaignId } = this.state;

    dispatchFetchInfluncerMetrics(campaignId);
    dispatchFetchSubcampaigns(campaignId);
    dispatchFetchDeliverableTags(campaignId, deliverableTypeFilter, selectedSubcampaignId, contentApprovalV2);
    if (contentApprovalV2) {
      dispatchFetchCampaignContentApprovalDeliverables(campaignId);
    }
    this.fetchDeliverables(selectedSubcampaignId, deliverableTypeFilter, sort, filter, page);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const {
      campaignId,
      subcampaignsList,
      subcampaignId,
      deliverables,
      deliverableTags,
      contentApprovalV2,
      dispatchFetchDeliverableTags,
      dispatchFetchCampaignContentApprovalDeliverables
    } = this.props;

    const {
      visibleSubcampaigns,
      selectedSubcampaignId,
      selectedSubcampaignCreator,
      deliverableTypeFilter,
      sort,
      filter,
      page
    } = this.state;

    if (contentApprovalV2 !== prevProps.contentApprovalV2) {
      dispatchFetchDeliverableTags(campaignId, deliverableTypeFilter, selectedSubcampaignId, contentApprovalV2);
      this.fetchDeliverables(selectedSubcampaignId, deliverableTypeFilter, sort, filter, page);
      if (contentApprovalV2) {
        dispatchFetchCampaignContentApprovalDeliverables(campaignId);
      }
    }

    // todo update and maybe update higher -> request by offer
    if (subcampaignsList !== prevProps.subcampaignsList) {
      const nextVisibleSubcampaigns = visibleSubcampaigns && visibleSubcampaigns.length > 0 ?
        visibleSubcampaigns :
        (subcampaignsList || []).filter(isAccepted);

      let nextSelectedSubcampaignId = selectedSubcampaignId || null;
      let nextSelectedSubcampaignCreator = selectedSubcampaignCreator || null;

      if (!nextSelectedSubcampaignCreator && nextVisibleSubcampaigns.length > 0 && selectedSubcampaignId === subcampaignId) { // from URL `?subcampaignId=<id>`
        const selectedSubcampaign = nextVisibleSubcampaigns.find(s => s.id === subcampaignId);
        if (selectedSubcampaign && selectedSubcampaign.creatorProfile) {
          nextSelectedSubcampaignCreator = selectedSubcampaign.creatorProfile;
        } else {
          nextSelectedSubcampaignCreator = null;
          nextSelectedSubcampaignId = null;
        }
      }

      this.setState(state => ({
        selectedSubcampaignId: nextSelectedSubcampaignId,
        selectedSubcampaignCreator: nextSelectedSubcampaignCreator,
        visibleSubcampaigns: nextVisibleSubcampaigns,
        subcampaignsMapByInfluencer: isEmpty(state.subcampaignsMapByInfluencer) ?
          nextVisibleSubcampaigns.reduce((res, subcampaign) => {
            res[subcampaign.creatorProfile.id] = subcampaign;
            return res;
          }, {}) :
          state.subcampaignsMapByInfluencer
      }));
    }

    if (visibleSubcampaigns !== prevState.visibleSubcampaigns) {
      const nextFilterOptions = this.buildFilterOptions(visibleSubcampaigns);

      if (!isEqual(nextFilterOptions, prevState.filterOptions)) {
        this.setState({
          filterOptions: nextFilterOptions
        });
      }
    }

    if (deliverables !== prevProps.deliverables) {
      const previousMostActiveState = getMostActiveState(prevState.expandedIdsMap);
      const previousMostActiveView = getMostActiveState(prevState.selectedViewIdsMap) as DeliverableV2TabKey | null;
      const viewItemsToIgnore = getDeliverableSelectViewItemsToIgnore(this.props.deliverables);
      const defaultView = viewItemsToIgnore.includes('content_approval')
        ? 'information'
        : 'content_approval';
      const selectedView = previousMostActiveView === null
      || viewItemsToIgnore.includes(previousMostActiveView) ? defaultView : previousMostActiveView;

      const deliverableTab = getUrlParam('tab') as DeliverableV2TabKey;
      const deliverableId = getHash('deliverable-');

      if (deliverables.length) {
        this.setState({
          fetchTriggered: false,
          expandedIdsMap: this.getExpandedIdsMap(
            previousMostActiveState === null ? true : previousMostActiveState, deliverables
          ),
          selectedViewIdsMap: this.getSelectedViewIdsMap(
            selectedView,
            deliverables,
            false,
            deliverableId,
            deliverableTab
          )
        });
      } else {
        this.setState({
          fetchTriggered: false
        });
      }
    }

    if (deliverableTags !== prevProps.deliverableTags) {
      this.setState({
        tags: this.getTags(deliverableTags.filter(tag => tag.name))
      });
    }
  }

  componentWillUnmount(): void {
    const {
      dispatchResetDeliverables,
      dispatchResetSubcampaigns,
      dispatchResetCampaignContentApprovalDeliverables
    } = this.props;

    dispatchResetDeliverables();
    dispatchResetCampaignContentApprovalDeliverables();
    dispatchResetSubcampaigns();
  }

  getSelectedViewIdsMap(
    selectedView: DeliverableV2TabKey,
    deliverables: Array<DeliverableCampaign>,
    forceUpdate?: boolean,
    deliverableId?: number | null,
    deliverableTab?: DeliverableV2TabKey | null
  ): { [id: number]: DeliverableV2TabKey } {
    return deliverables.reduce((prevValue, deliverable) => {
      const { selectedViewIdsMap } = this.state;

      if (selectedViewIdsMap[deliverable.id] !== undefined && !forceUpdate) {
        // eslint-disable-next-line no-param-reassign
        prevValue[deliverable.id] = selectedViewIdsMap[deliverable.id];
      } else {
        // eslint-disable-next-line no-param-reassign
        prevValue[deliverable.id] = deliverableTab && (deliverableId === deliverable.id) ? deliverableTab :
          selectedView;
      }

      return prevValue;
    }, {});
  }

  getTags(tags: Array<{ name: string, color: DeliverableTagColor }>): Array<MixedSelectDropdownItem> {
    return tags.map((tag, index) => ({
      id: index,
      identifier: `${index}-${tag.name}`,
      label: tag.name,
      isSelected: false,
      isDisabled: false,
      isFocused: false,
      value: tag.name,
      color: TASK_TAGS_COLORS[tag.color || '']
    }));
  }

  buildFilterOptions(subcampaigns: Array<Subcampaign>): Array<SelectOption> {
    const counts = FILTER_OPTIONS.map(filter => ({
      count: (filter === 'all'
        ? subcampaigns.reduce((sum, s) => sum + getDeliverablesCountFromOffer(s.offer) - getDeliverablesCountByTypeFromOffer(s.offer, DELIVERABLE_TYPE_CONTENT_PRODUCTION), 0)
        : subcampaigns.reduce((sum, s) => sum + getDeliverablesCountByTypeFromOffer(s.offer, filter), 0)),
      filter
    }));

    return counts.filter(item => item.count > 0).map(item => ({
      label: `${__t(`campaign_deliverables.task_filter_options.${item.filter}`)} (${item.count})`,
      value: item.filter
    }));
  }

  fetchDeliverables = async (
    selectedSubcampaignId: number | undefined | null,
    deliverableTypeFilter: string,
    sort: string,
    filter: string | undefined,
    page: number,
    tags?: Array<string>
  ): Promise<void> => {
    const {
      campaignId,
      contentApprovalV2,
      dispatchFetchCampaignDeliverables
    } = this.props;

    await dispatchFetchCampaignDeliverables(
      campaignId,
      page,
      selectedSubcampaignId,
      deliverableTypeFilter,
      sort,
      filter,
      tags,
      contentApprovalV2
    );
  };

  getExpandedIdsMap = (
    isExpanded: boolean | string,
    deliverables: Array<DeliverableCampaign>,
    forceUpdate?: boolean
  ): { [id: number]: boolean} => deliverables.reduce((prevValue, deliverable) => {
    const { expandedIdsMap } = this.state;

    // eslint-disable-next-line no-param-reassign
    prevValue[deliverable.id] = expandedIdsMap[deliverable.id] !== undefined && !forceUpdate ?
      expandedIdsMap[deliverable.id] : isExpanded;

    return prevValue;
  }, {});

  handleContentMoved = async () => {
    const { deliverableTypeFilter, selectedSubcampaignId, sort, filter, page } = this.state;
    await this.fetchDeliverables(selectedSubcampaignId, deliverableTypeFilter, sort, filter, page);
  };

  handleExpandAllChange = (isExpanded: boolean): void => {
    const { deliverables } = this.props;

    this.setState({
      expandedIdsMap: this.getExpandedIdsMap(isExpanded, deliverables, true)
    });
  };

  handleExpandDeliverable = (deliverableId: number): void => {
    this.setState(prevState => ({
      ...prevState,
      expandedIdsMap: {
        ...prevState.expandedIdsMap,
        [deliverableId]: !prevState.expandedIdsMap[deliverableId]
      }
    }));
  };

  handleSortItems = (items: Array<Subcampaign>) =>
    sortBy<Subcampaign>(items, ['creatorProfile.name']);

  handleItemSelected = (item?: Subcampaign | null) => {
    const {
      campaignId,
      contentApprovalV2,
      dispatchFetchDeliverableTags,
      dispatchFetchCampaignContentApprovalDeliverables
    } = this.props;
    const { deliverableTypeFilter, sort, filter } = this.state;
    const page = 1;
    const selectedSubcampaignId = item ? item.id : null;
    const selectedSubcampaignCreator = item ? item.creatorProfile : null;

    this.setState({
      selectedSubcampaignId,
      selectedSubcampaignCreator,
      page,
      fetchTriggered: true
    });


    this.fetchDeliverables(selectedSubcampaignId, deliverableTypeFilter, sort, filter, page);
    dispatchFetchDeliverableTags(campaignId, deliverableTypeFilter, selectedSubcampaignId, contentApprovalV2);
    if (contentApprovalV2) {
      dispatchFetchCampaignContentApprovalDeliverables(campaignId, selectedSubcampaignId);
    }
  };

  handleFilterChanged = (deliverableTypeFilter: string) => {
    const { selectedSubcampaignId, sort, filter } = this.state;
    const {
      campaignId,
      contentApprovalV2,
      dispatchFetchDeliverableTags,
      dispatchFetchCampaignContentApprovalDeliverables
    } = this.props;
    const page = 1;

    this.setState({
      deliverableTypeFilter,
      page,
      fetchTriggered: true
    });

    dispatchFetchDeliverableTags(campaignId, deliverableTypeFilter, null, contentApprovalV2);
    this.fetchDeliverables(selectedSubcampaignId, deliverableTypeFilter, sort, filter, page);
    if (contentApprovalV2) {
      dispatchFetchCampaignContentApprovalDeliverables(campaignId, selectedSubcampaignId);
    }
  };

  handleSortChanged = (sort: string) => {
    const { tagsAsStrings, selectedSubcampaignId, deliverableTypeFilter, filter } = this.state;
    const tags = !isEmpty(tagsAsStrings) ? tagsAsStrings : undefined;
    const page = 1;

    this.setState({
      sort,
      page,
      fetchTriggered: true
    });

    this.fetchDeliverables(selectedSubcampaignId, deliverableTypeFilter, sort, filter, page, tags);
  };

  handleListFilterChanged = (filter?: string) => {
    const { tagsAsStrings, deliverableTypeFilter, selectedSubcampaignId, sort } = this.state;
    const tags = !isEmpty(tagsAsStrings) ? tagsAsStrings : undefined;
    const page = 1;

    this.setState({
      filter,
      page,
      fetchTriggered: true
    });

    this.fetchDeliverables(selectedSubcampaignId, deliverableTypeFilter, sort, filter, page, tags);
  };

  handleShouldFilterOutItem = (subcampaign: Subcampaign, deliverableTypeFilter: string) => {
    if (deliverableTypeFilter === 'all') {
      return !(getDeliverablesCountFromOffer(subcampaign.offer) - getDeliverablesCountByTypeFromOffer(subcampaign.offer, DELIVERABLE_TYPE_CONTENT_PRODUCTION) > 0);
    }

    return !(getDeliverablesCountByTypeFromOffer(subcampaign.offer, deliverableTypeFilter) > 0);
  };

  handleContentDownload = (items: Array<ActionButtonDropdownItem>): void => {
    const selectedItemIdentifier = items.find((item) => item.isSelected)?.identifier;

    if (selectedItemIdentifier === DownloadButtonType.ALL) {
      this.handleDownload();
    }

    if (selectedItemIdentifier === DownloadButtonType.APPROVED) {
      this.handleDownloadAllApproved();
    }
  };

  handleDownload = () => {
    const { campaignId, campaignName, dispatchDownloadCampaignContent, contentApprovalV2 } = this.props;
    const { selectedSubcampaignId, selectedSubcampaignCreator, deliverableTypeFilter } = this.state;

    dispatchDownloadCampaignContent(
      buildPrefixForDownloadableCampaignContent(campaignId, campaignName, selectedSubcampaignCreator),
      campaignId,
      selectedSubcampaignId,
      contentApprovalV2 ? 'all' : deliverableTypeFilter
    );
  };

  handleDownloadAllApproved = () => {
    const { campaignId, campaignName, dispatchDownloadAllApprovedCampaignContent, contentApprovalV2 } = this.props;
    const { selectedSubcampaignId, selectedSubcampaignCreator, deliverableTypeFilter } = this.state;
    const deliverableType = deliverableTypeFilter === 'all' ? null : deliverableTypeFilter;

    dispatchDownloadAllApprovedCampaignContent(
      buildPrefixForDownloadableCampaignContent(campaignId, campaignName, selectedSubcampaignCreator),
      campaignId,
      selectedSubcampaignId,
      contentApprovalV2 ? 'all' : deliverableType
    );
  };

  handlePageChanged = (page: number) => {
    const { selectedSubcampaignId, deliverableTypeFilter, sort, filter } = this.state;

    this.setState({
      page,
      fetchTriggered: true
    });

    this.fetchDeliverables(selectedSubcampaignId, deliverableTypeFilter, sort, filter, page);

    if (this.containerRef.current) {
      this.containerRef.current.scrollIntoView();
    }
  };

  handleTabChanged = (activeTab?: string | null) => {
    this.handleFilterChanged(activeTab === DELIVERABLE_TYPE_CONTENT_PRODUCTION ? DELIVERABLE_TYPE_CONTENT_PRODUCTION : 'all');
  };

  handleTagFilterChange = (updatedList: Array<MixedSelectDropdownItem>): void => {
    const { deliverableTypeFilter, sort, filter, selectedSubcampaignId } = this.state;
    const tags = updatedList.reduce((filteredTags, tag) => {
      if (tag.isSelected && tag.color) {
        filteredTags.push(`${tag.value}-${TASK_TAGS_COLORS_REVERSE[tag.color]}`);
      }

      return filteredTags;
    }, [] as Array<string>);

    this.setState(prevState => ({
      ...prevState,
      tags: updatedList,
      tagsAsStrings: tags,
      page: 1,
      fetchTriggered: true
    }));

    this.fetchDeliverables(selectedSubcampaignId, deliverableTypeFilter, sort, filter, 1, tags);
  };

  handleDeliverableChangeView = (deliverableId, selectedView: DeliverableV2TabKey) => {
    this.setState(prevState => ({
      ...prevState,
      selectedViewIdsMap: {
        ...prevState.selectedViewIdsMap,
        [deliverableId]: selectedView
      }
    }));
  };

  handleChangeView = (selectedView: DeliverableV2TabKey) => {
    const { deliverables, contentApprovalV2 } = this.props;

    if (contentApprovalV2) {
      replaceHistoryStateWithDefault();
    }

    this.setState(prevState => ({
      ...prevState,
      selectedViewIdsMap: this.getSelectedViewIdsMap(selectedView, deliverables, true)
    }));
  };

  renderSpinner(withCover: boolean): React.ReactNode {
    return (
      <div className="campaign-deliverables__spinner-container">
        <Spinner
          position={(withCover) ? 'cover' : 'inline'}
          size="small"
          color="light"
        />
      </div>
    );
  }

  renderInfluencerSelector(activeTab: string): React.ReactNode {
    const { isDeliverablesLoading, isSubcampaignsLoading, contentApprovalV2 } = this.props;
    const {
      selectedSubcampaignId,
      visibleSubcampaigns,
      deliverableTypeFilter,
      fetchTriggered,
      filterOptions
    } = this.state;

    return (
      <CampaignInfluencerSelector
        key={`subcampaigns-for-tab-${activeTab}`}
        isLoading={isSubcampaignsLoading}
        disabled={isDeliverablesLoading || fetchTriggered}
        defaultSelectedItemId={selectedSubcampaignId}
        defaultCurrentFilter={deliverableTypeFilter}
        filterOptions={deliverableTypeFilter === DELIVERABLE_TYPE_CONTENT_PRODUCTION ? undefined : filterOptions}
        items={visibleSubcampaigns}
        resetButton={resetButtonProps}
        shouldFilterOut={!contentApprovalV2 ? this.handleShouldFilterOutItem : undefined}
        onItemSelected={this.handleItemSelected}
        onFilterChanged={this.handleFilterChanged}
        onSortItems={this.handleSortItems}
      />
    );
  }

  renderDownloadButton(): React.ReactNode {
    const { deliverables } = this.props;
    const { deliverableTypeFilter } = this.state;

    if (!deliverables.length) {
      return null;
    }

    if (deliverableTypeFilter === DELIVERABLE_TYPE_CONTENT_PRODUCTION) {
      return (
        <ActionButtonDropdown
          color="secondary"
          onClick={this.handleContentDownload}
          label={__t('campaign_deliverables.button_download_label')}
          items={[
            {
              identifier: DownloadButtonType.ALL,
              value: DownloadButtonType.ALL,
              label: __t('campaign_deliverables.button_download_all'),
              isDisabled: false,
              isFocused: false,
              isSelected: false
            },
            {
              identifier: DownloadButtonType.APPROVED,
              value: DownloadButtonType.APPROVED,
              label: __t('campaign_deliverables.button_download_approved'),
              isDisabled: false,
              isFocused: false,
              isSelected: false
            }
          ]}
          name="download-content"
          customSize={{
            width: 144
          }}
        />
      );
    }

    return (
      <ActionButton
        color="secondary"
        onClick={this.handleDownload}
      >
        {__t('campaign_deliverables.button_download_all')}
      </ActionButton>
    );
  }

  renderTaskControls(isLoading: boolean): React.ReactNode {
    const { contentApprovalV2, deliverables } = this.props;
    const { sort, filter, tags, selectedViewIdsMap, expandedIdsMap } = this.state;
    const filterLinksToIgnore = getDeliverableSelectViewItemsToIgnore(deliverables);
    const selectViewItems = getDeliverablesSelectViewItems(filterLinksToIgnore);
    const zoutfitsOnly = isZalandoOutfitOnly(deliverables);

    return (
      <CampaignDeliverablesTaskControl
        isForBrand
        isLoading={isLoading}
        contentApprovalV2={contentApprovalV2}
        sortOptions={SORT_VALUES}
        sort={sort}
        filter={filter}
        tags={tags}
        selectedViewIdsMap={selectedViewIdsMap}
        expandedIdsMap={expandedIdsMap}
        selectViewItems={selectViewItems}
        zoutfitsOnly={zoutfitsOnly}
        onTagFilterChange={this.handleTagFilterChange}
        onSortChanged={this.handleSortChanged}
        onListFilterChanged={this.handleListFilterChanged}
        onChangeView={this.handleChangeView}
        onExpandAllChange={this.handleExpandAllChange}
      />
    );
  }

  renderHeaderControls(): React.ReactNode {
    const { selectedSubcampaignCreator } = this.state;
    const { contentApprovalLink } = this.props;

    return (
      <div className="campaign-deliverables__header-controls">
        <div className="campaign-deliverables__header">
          {selectedSubcampaignCreator
            ? `${__t('campaign_deliverables.from_a_creator')} @${String(selectedSubcampaignCreator.name)}`
            : __t('campaign_deliverables.from_all_creators')}
        </div>
        <div className="campaign-deliverables_header-controls">
          {
            contentApprovalLink && (
              <div className="client-approval-page-link">
                <Tooltip
                  showQuestionMark
                  width="extra-long"
                  textAlign="left"
                  message={__t('campaign_deliverables.client_approval_page_link_tooltip')}
                  classNameWrapper="client-approval-page-link__tooltip"
                >
                  <ActionLink
                    className="client-approval-page-link__text"
                    color="secondary"
                    link={contentApprovalLink}
                    openInNewTab
                  >
                    {__t('campaign_deliverables.open_client_page')}
                  </ActionLink>
                </Tooltip>
              </div>
            )
          }
          {this.renderDownloadButton()}
        </div>
      </div>
    );
  }

  renderContent(): React.ReactNode {
    const { campaignId, deliverables, pagination, location, isDeliverablesLoading, contentApprovalV2 } = this.props;
    const { subcampaignsMapByInfluencer, fetchTriggered, expandedIdsMap, selectedViewIdsMap } = this.state;

    const overlayClasses = classnames('campaign-deliverables__content-overlay', {
      'campaign-deliverables__content-overlay--is-visible': isDeliverablesLoading || fetchTriggered
    });

    return (
      <div className="campaign-deliverables__content">
        <CampaignDeliverablesList
          campaignId={campaignId}
          deliverables={deliverables}
          isDeliverablesLoading={isDeliverablesLoading}
          expandedIdsMap={expandedIdsMap}
          subcampaignsMapByInfluencer={subcampaignsMapByInfluencer}
          selectedViewIdsMap={selectedViewIdsMap}
          contentApprovalV2={contentApprovalV2}
          onContentMoved={this.handleContentMoved}
          onTrackDeliverableUploadEvent={
            eventAction => trackEventDeliverableUploaded(eventAction, 'task cards on offer page')
          }
          onExpanded={(deliverableId) => this.handleExpandDeliverable(deliverableId)}
          onViewChange={(deliverableId: number, selectedView: DeliverableV2TabKey) =>
            this.handleDeliverableChangeView(deliverableId, selectedView)}
        />
        <Pagination
          location={location}
          meta={pagination}
          links={pagination.links}
          onClick={this.handlePageChanged}
          scrollToTopOnPageChange={false}
        />
        <div className={overlayClasses}>
          {this.renderSpinner(isEmpty(deliverables) && isDeliverablesLoading)}
        </div>
      </div>
    );
  }

  render(): React.ReactNode {
    const { isDeliverablesLoading, isSubcampaignsLoading, contentApprovalV2 } = this.props;
    const { selectedViewIdsMap, deliverableTypeFilter, fetchTriggered } = this.state;

    if (isNil(contentApprovalV2)) {
      return null;
    }

    if (isNil(contentApprovalV2)) {
      return null;
    }

    const headerClass = classnames('campaign-deliverables__heading', {
      'campaign-deliverables__heading--alt': contentApprovalV2
    });

    let activeTab = deliverableTypeFilter === DELIVERABLE_TYPE_CONTENT_PRODUCTION
      ? DELIVERABLE_TYPE_CONTENT_PRODUCTION
      : 'rest';

    if (contentApprovalV2) {
      activeTab = getMostActiveState(selectedViewIdsMap) || '';
    }

    return (
      <section
        className="campaign-deliverables"
        ref={this.containerRef}
      >
        <PageHeading
          heading={__t('campaign_deliverables.title')}
          margin="none"
          size="h1"
          color="light"
          customClass={headerClass}
        />
        {
          !contentApprovalV2 && (
            <div className="campaign-deliverables__tabs">
              <TabsSection
                withShadow
                items={TABS}
                activeTab={activeTab}
                onClick={this.handleTabChanged}
              />
            </div>
          )
        }
        {this.renderInfluencerSelector(activeTab)}
        {this.renderHeaderControls()}
        {this.renderTaskControls(isDeliverablesLoading || isSubcampaignsLoading || fetchTriggered)}
        {this.renderContent()}
      </section>
    );
  }
}

const mapStateToProps = (state): StateProps => {
  const {
    campaignDeliverables,
    campaignSubcampaigns,
    campaignBrief,
    campaignOverview,
    router
  } = state;

  return ({
    subcampaignsList: campaignSubcampaigns.list,
    deliverables: campaignDeliverables.deliverables,
    pagination: campaignDeliverables.pagination,
    location: router.location,
    isSubcampaignsLoading: campaignSubcampaigns.isLoading,
    isDeliverablesLoading: campaignDeliverables.isLoading,
    campaignName: campaignBrief.brief && campaignBrief.brief.name || '',
    contentApprovalLink: contentApprovalLinkSelector(state),
    deliverableTags: campaignDeliverables.deliverableTags,
    contentApprovalV2: campaignOverview.overview?.contentApprovalV2
  });
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDownloadCampaignContent: (prefix: string, campaignId: number, subcampaignId?: number | null, deliverableType?: string | null) =>
    dispatch(downloadCampaignContent(prefix, campaignId, subcampaignId, deliverableType, false)),

  dispatchDownloadAllApprovedCampaignContent: (prefix: string, campaignId: number, subcampaignId?: number | null, deliverableTypeFilter?: string | null) =>
    dispatch(downloadCampaignContent(prefix, campaignId, subcampaignId, deliverableTypeFilter, true)),

  dispatchFetchCampaignDeliverables: (
    campaignId: number,
    page?: number | null,
    subcampaignId?: number | null,
    deliverableType?: string | null,
    sort?: string | null,
    filter?: string,
    tags?: Array<string>,
    contentApprovalV2?: boolean) =>
    dispatch(fetchCampaignDeliverables(
      campaignId,
      page,
      subcampaignId,
      deliverableType,
      sort,
      filter,
      undefined,
      undefined,
      tags,
      contentApprovalV2)),

  dispatchResetDeliverables: () => dispatch(resetDeliverables()),
  dispatchFetchCampaignContentApprovalDeliverables: (campaignId: number, selectedSubcampaignId?: number | null) => (
    dispatch(fetchCampaignContentApprovalDeliverables(campaignId, selectedSubcampaignId))
  ),
  dispatchResetCampaignContentApprovalDeliverables: () => dispatch(resetCampaignContentApprovalDeliverables()),
  dispatchFetchSubcampaigns: (campaignId: number) => dispatch(fetchSubcampaigns(campaignId, {
    showLoadingComponent: false,
    includeDeliverablesOverview: true
  })),
  dispatchResetSubcampaigns: () => dispatch(resetSubcampaigns()),
  dispatchFetchInfluncerMetrics: (campaignId: number) => dispatch(fetchInfluncerMetrics(campaignId)),
  dispatchFetchDeliverableTags: (
    campaignId: number,
    deliverableType?: string | null,
    subcampaignId?: number | null,
    contentApprovalV2?: boolean
  ) =>
    dispatch(fetchDeliverableTags(campaignId, deliverableType, subcampaignId, contentApprovalV2))
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignDeliverables);
